<template>
  <!-- eslint-disable max-len -->
  <div>
    <main class="instant-savings-mobile u-container">
      <h1 class="font-bold text-4xl">
        USPS Change-of-Address<sup>®</sup>
      </h1>
      <ProgressBar
        v-if="!showChoiceExperience"
        :steps="6"
        :current="5"
      />
      <IndividualProgressBarWithSteps
        v-if="showChoiceExperience"
        :mobile="true"
      />
      <h2
        ref="stepHeading"
        class="mt-2 text-xl"
      >
        <template v-if="!showChoiceExperience">
          Step 5:&nbsp;
        </template>
        <span class="font-bold">Would you like to receive special offers?</span>
      </h2>
      <p class="mt-4 text-base">
        Select "Yes" to get all the offers below delivered to <strong v-if="email">{{ email }}</strong><strong v-else>the email provided</strong> in a
        <strong>one-time email from USPS</strong> after you complete your USPS Change-of-Address.
      </p>
      <form
        ref="form"
        novalidate
        @submit.prevent="submit"
      >
        <div class="flex mt-6">
          <button
            id="offers-yes-btn"
            ref="offersYesBtn"
            type="button"
            :class="{ 'c-btn--secondary': offersOptIn !== 'YES' }"
            class="c-btn w-1/2"
            @click="selectOffersOptIn('YES')"
          >
            Yes
          </button>
          <button
            id="offers-no-btn"
            ref="offersNoBtn"
            type="button"
            :class="{ 'c-btn--secondary': offersOptIn !== 'NO' }"
            class="c-btn w-1/2 ml-4"
            @click="selectOffersOptIn('NO')"
          >
            No
          </button>
        </div>
        <p
          v-if="offersOptInError"
          class="errors text-red-200 text-sm font-bold mt-2"
        >
          Please make a selection.
        </p>
        <div
          v-if="offers && offers.length"
          class="flex flex-col items-center mt-6"
        >
          <component
            :is="getAmpComponent(template.schema.name)"
            v-for="({ accountId, accountName, adGroupId, id, template }, objectId) in offers"
            ref="offerTile"
            :key="id"
            :value="selectedOffers.includes(offers[objectId])"
            :account-id="accountId"
            :account-name="accountName"
            :ad-id="id"
            :ad-group-id="adGroupId"
            :alt-text="accountName"
            :background-url="template.properties.backgroundUrl"
            :cta="template.properties.cta"
            :headline="template.properties.headline"
            :legal-text="template.properties.legalText"
            :logo-url="template.properties.logoUrl"
            :short-description="template.properties.shortDescription"
            :tooltip="template.properties.tooltip"
            :template-id="template.id"
            class="instant-savings-mobile__offer-tile disable-hover"
            @selected:offer="toggleOfferSelect(offers[objectId])"
          />
        </div>
        <p class="mt-6 text-xs">
          Neither the U.S. Postal Service<sup>®</sup> nor any contractor associated with the operation of this website: (1) make any guarantee as to the accuracy of the statements made by the advertisers or the content of the editorial material on this website; (2) endorse any company, product or service listed or advertised in this MoversGuide Online™ product; (3) will be held liable in any manner for any claim, loss, expense, damage or consumer dissatisfaction arising out of or in connection with the use of the MoversGuide Online™ product; and (4) the advertising materials are free to the public and produced without the use of either tax or postal revenue.
        </p>
        <div class="c-hairline mt-6" />
        <StickyPanel class="mt-6">
          <template slot="buttons">
            <button
              id="next"
              ref="nextBtn"
              class="c-btn--mobile simplify__nav-button"
              :class="{ 'c-btn--disabled': !offersOptIn }"
              @click="analytics.click.next()"
            >
              Next
            </button>
          </template>
        </StickyPanel>
        <section class="py-6">
          <p><strong>Request for Change-of-Address and Forwarding Services</strong></p>
          <p class="mt-2 mb-4 text-sm">
            <strong>Privacy Act Statement:</strong>  Your information will be used to provide mail forwarding
            and Change-of-Address (COA) services and move related advertisements, to verify your identity,
            to facilitate communication, to facilitate surveys regarding the COA process, to identify and mitigate potential fraud,
            and to provide address correction services. Collection is authorized by 39 U.S.C. 401, 403, 404.
            Providing the information is voluntary, but if not supplied, we may not be able to process and
            fulfill your request for services. We do not disclose your information to third parties without
            your consent, except to facilitate the transaction, to act on your behalf or request, or as
            legally required.
            <button
              v-if="hidePrivacyActContent"
              class="c-link"
              type="button"
              @click="hidePrivacyActContent = false"
            >
              Click here to display the entire Privacy Act Statement
            </button>
            <template v-else>
              This includes the following standard and specific routine uses: Incident to legal proceedings;
              for law enforcement purposes; to a congressional office; to agents or contractors; to auditors;
              to labor organizations; to federal, state, local, or foreign government agencies; to agencies
              and entities for financial matters; for customer service purposes; to a federal, state, or
              local government agency upon certification when required for the performance of its duties;
              to a law enforcement agency after the Postal Inspection Service has confirmed that the COA
              information is needed for a criminal investigation; for service of process; for purpose of
              jury service; to agencies or entities at customer’s request; to a disaster relief organization;
              pursuant to the order of a court of competent jurisdiction; for address correction services
              only if the mailer is in possession of the name and old address; and to USPS licensed service
              providers to perform mailing list address correction services for lists containing your name
              and old address. For a list of licensed service providers please visit <a
                href="https://postalpro.usps.com/NCOALink_Service_Providers"
                class="c-link--inline"
              >https://postalpro.usps.com/NCOALink_Service_Providers</a>. For more information regarding our
              privacy policies visit <a
                href="https://www.usps.com/privacypolicy"
                target="_blank"
                class="c-link--inline"
                rel="noopener noreferrer"
              >www.usps.com/privacypolicy</a>.
              <button
                class="c-link"
                type="button"
                @click="hidePrivacyActContent = true"
              >
                Display Less
              </button>
            </template>
          </p>
        </section>
      </form>
    </main>
  </div>
</template>

<script>
import { AdvantageDouble, AdvantageSingle } from '@ampersend/mgo';
import {
  ProgressBar,
  IndividualProgressBarWithSteps,
} from '@mgo/ui/lib/components';
import { mobileAnalytics } from './analytics';
import InstantSavingsMixin from './mixin';
import StickyPanel from '../../components/StickyPanel.vue';

export default {
  name: 'MobileInstantSavings',
  components: {
    AdvantageDouble,
    AdvantageSingle,
    StickyPanel,
    ProgressBar,
    IndividualProgressBarWithSteps,
  },
  mixins: [InstantSavingsMixin],
  data() {
    return {
      hidePrivacyActContent: true,
    };
  },
  computed: {
    showNonControlExperience: () => JSON.parse(sessionStorage.getItem('showNonControlExperience')) ?? false,
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    analytics() {
      return mobileAnalytics();
    },
    schemaNames() {
      return ['MGO Advantage Single'];
    },
  },
};
</script>
